import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getRevenueByPaymentDate } from '../../services'
import { RevenueAllocationsParams, RevenueCombined } from '../../types'
import { getTableColumns } from './table-columns'

interface PaymentDateTableProps {
    filters: RevenueAllocationsParams
    search?: string
}

const PaymentDateTable: React.FC<PaymentDateTableProps> = ({
    filters,
    search,
}) => {
    const { t } = useTranslation()
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(50)

    // Convert gte/lte (YYYY-MM) to from_date/to_date (YYYY-MM-DD)
    const apiParams = useMemo(() => {
        const fromDate = filters.gte
            ? dayjs(filters.gte).startOf('month').format('YYYY-MM-DD')
            : undefined
        const toDate = filters.lte
            ? dayjs(filters.lte).endOf('month').format('YYYY-MM-DD')
            : undefined

        return {
            from_date: fromDate,
            to_date: toDate,
            product_group_names: filters.product_group_names,
            account_manager_codes: filters.account_manager_codes,
            sale_manager_codes: filters.sale_manager_codes,
            is_allocated: filters.is_allocated,
            search: search || undefined,
            page,
            page_size: pageSize,
            sort_by: 'payment_date',
            sort_order: 'desc' as const,
        }
    }, [filters, search, page, pageSize])

    const {
        data: response,
        isLoading,
        isFetching,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORTS_BI_REVENUE_BY_PAYMENT_DATE, apiParams],
        queryFn: () => getRevenueByPaymentDate(apiParams),
        staleTime: 5 * 60 * 1000,
    })

    const data = response?.results || []
    const pagination = response?.pagination

    const columns = useMemo(() => getTableColumns(t), [t])

    return (
        <DataTableCustomized<RevenueCombined>
            columns={columns}
            rows={data}
            loading={isLoading}
            isFetching={isFetching && !isLoading}
            pinnedHeader
            maxHeight="calc(100vh - 380px)"
            rowKey={(row, index) =>
                `${row.invoice_id || 'paid'}-${row.revenue_month}-${row.service_id}-${index}`
            }
            resizable
            pagination={{
                page,
                pageSize,
                total: pagination?.total || 0,
                onPageChange: setPage,
                onPageSizeChange: (size) => {
                    setPageSize(size)
                    setPage(1)
                },
                pageSizeOptions: [20, 50, 100],
            }}
            emptyMessage={t('no_data') || 'Không có dữ liệu'}
        />
    )
}

export default PaymentDateTable
